import type { AnalyticDateFilter } from '~/presentation/ui';

import { useNavigateToOrderListing } from '~/modules/order-analytic/hooks/useNavigateToOrderListing';
import { useControlSettingStore } from '~/presentation/reports/common/ReportFilter/stores';
import { ORDER_LISTING_DATA_PRESET } from '~/modules/order-analytic/presets/order-listing';
import { EControlSettingType, EReportControlId } from '~/presentation/reports/common/type';
import { EReportParamKeys, EReportSource } from '~/modules/report-analytic/types/report';
import { useOrderControlStore } from '~/modules/order-analytic/stores/order-control';
import { getAnalyticModeFromSearchParams } from '~/modules/report-analytic/utils';
import { useControlDataChange } from '~/presentation/reports/common';
import { useLocation, useSearchParams } from '@remix-run/react';
import { useDateTimePickerContext } from '~/presentation/ui';
import { LOCATION_PATH_KEY } from '~/modules/page-analytic';
import { EOrderViewScope } from '~/modules/order-analytic';
import { CAMPAIGN_TYPE } from '~/constants/campaign';
import { INavigateKey } from '~/types/navigate';
import { ROUTE_PATH } from '~/config/navigate';

export const useViewAllStoreOrders = () => {
  const resetControlSettingUI = useControlSettingStore((state) => state.resetControlSettingUI);
  const setOrderControlData = useOrderControlStore((state) => state.setOrderControlData);

  const { dateTimePicked, setDateTimePicked } = useDateTimePickerContext();

  const { emitControlChange } = useControlDataChange();

  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get(EReportParamKeys.CAMPAIGN_ID) ?? '';
  const campaignType = searchParams.get(EReportParamKeys.CAMPAIGN_TYPE) ?? '';
  const locationPathKey = searchParams.get(LOCATION_PATH_KEY);
  const from = searchParams.get(EReportParamKeys.FROM);
  const mode = getAnalyticModeFromSearchParams(searchParams);

  const { handleNavigateToOrderListing } = useNavigateToOrderListing();

  const isOnOrderListingPage = pathname.includes(INavigateKey.OrderListing);
  const isOnReportPage = pathname.includes(INavigateKey.Reports);

  const handleViewAllStoreOrders = () => {
    const isFilteredByVersion = dateTimePicked.title.toLowerCase().includes('version');
    const customDateInfo = isFilteredByVersion ? { alias: 'custom', title: 'Custom' } : undefined;

    if (isOnReportPage) return handleNavigateToOrderListingFromReport(customDateInfo);

    const handleClearControlSetting = () => {
      resetControlSettingUI(EControlSettingType.ORDER);
      setOrderControlData(ORDER_LISTING_DATA_PRESET);
      emitControlChange({ [EReportControlId.FILTER]: [] });
    };

    if (!isOnOrderListingPage) {
      // handleClearControlSetting();
      return handleNavigateToOrderListing({
        locationPath: locationPathKey,
        customDateInfo,
        mode,
        viewScope: EOrderViewScope.STORE,
      });
    }

    customDateInfo && setDateTimePicked({ ...dateTimePicked, ...customDateInfo });
    handleClearControlSetting();
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return {
    handleViewAllStoreOrders,
  };

  function handleNavigateToOrderListingFromReport(customDateInfo?: Partial<AnalyticDateFilter>) {
    resetControlSettingUI(EControlSettingType.ORDER);

    if (from === EReportSource.CAMPAIGN) {
      const navigateKey =
        campaignType === CAMPAIGN_TYPE.TEMPLATE_BUNDLE
          ? INavigateKey.CampaignMultipage
          : INavigateKey.CampaignTemplate;
      const navigatePath = `${ROUTE_PATH[navigateKey]}/${campaignId}/analytics/${INavigateKey.OrderListing}`;

      return handleNavigateToOrderListing(
        { from: EReportSource.CAMPAIGN, customDateInfo, mode },
        navigatePath,
      );
    }

    const navigatePath = `${ROUTE_PATH[INavigateKey.Analytics]}/${INavigateKey.OrderListing}`;
    return handleNavigateToOrderListing({ customDateInfo, mode }, navigatePath);
  }
};
